/**
 * Which symbols this account may chart — decided here, in the UI, and nowhere
 * else (owner's decision, 2026-09-26; `docs/plans/auth-hot-path.md` §2).
 *
 * The server no longer checks a chart request's symbol. What stops a symbol
 * outside the user's plan is that the pop-up will not take one and a list will
 * not hold one — and both ask this module.
 *
 * ## "Allowed" is "in the symbol index"
 *
 * `/sym/symbols` is already narrowed to the caller's universe on the server,
 * so the index this browser holds *is* the set of symbols the plan covers
 * (`lib/symbol-index.ts`). A symbol absent from it is either not in the plan or
 * not a symbol at all, and the two deserve different words — so a refusal asks
 * `/sym/names`, which is *not* narrowed, whether the symbol exists.
 *
 * ## The wording (owner's call: sensible wording, no mock)
 *
 * | case | message |
 * |---|---|
 * | no such symbol | `No symbol “XYZ”.` |
 * | not in the plan | `“XYZ” isn't in your plan.` |
 *
 * A ratio names the leg that fails, as the server's error already does for a
 * missing leg: `“XYZ” in “AAPL/XYZ” isn't in your plan.` The pop-up adds
 * `Esc keeps TSLA.`, naming the symbol the chart will keep.
 */

import { fetchSymbolNames } from "../api/symbols";
import { isComposite, parseComposite } from "./composite";

/**
 * The legs of what was typed: the operands of a ratio, or the one ticker.
 *
 * Through `isComposite` with the resolver, because a hyphen can be part of a
 * ticker: `BRK-B` is one symbol, not `BRK` minus `B`.
 */
export function legsOf(value: string, holds: (symbol: string) => boolean): string[] {
  if (!isComposite(value, holds)) return [value];
  const parsed = parseComposite(value);
  return parsed ? parsed.operands : [value];
}

/**
 * The first leg the index does not hold, or `null` when every leg is allowed.
 *
 * `holds` is `symbolResolver(rows)`, which answers for either spelling
 * (`BRK-B` and `BRK.B`).
 */
export function firstOutside(value: string, holds: (symbol: string) => boolean): string | null {
  for (const leg of legsOf(value, holds)) {
    if (!holds(leg)) return leg;
  }
  return null;
}

/** The sentence for one refused leg. `exists` is `undefined` when unknown. */
export function refusalMessage(
  value: string,
  leg: string,
  exists: boolean | undefined,
  keeps?: string,
): string {
  const isRatio = leg !== value;
  const where = isRatio ? ` in “${value}”` : "";
  const body =
    exists === undefined
      ? `“${leg}”${where} isn't available.`
      : exists
        ? `“${leg}”${where} isn't in your plan.`
        : isRatio
          ? `No symbol “${leg}”${where}.`
          : `No symbol “${leg}”.`;
  return keeps ? `${body} Esc keeps ${keeps}.` : body;
}

/**
 * The refusal for `leg`, having asked whether it exists at all.
 *
 * One small request, and only on the refusal path — a symbol the index holds
 * never gets here.
 */
export async function refusalFor(value: string, leg: string, keeps?: string): Promise<string> {
  const names = await fetchSymbolNames([leg]).catch(() => undefined);
  const exists = names === undefined ? undefined : leg.toUpperCase() in names;
  return refusalMessage(value, leg, exists, keeps);
}
